/**
 * PDF'ten metin parçaları (kullanıcı kararı 21.10.2026: PDF.js).
 *
 * `pdfjs-dist` 6.4.299, tam sürümle sabit ve gözden geçirilmiş (kurulum
 * betiği, ağ çağrısı, dinamik kod yok). Eski tarayıcılar (iOS WebKit) için
 * `legacy` derlemesi. Yalnızca gerektiğinde yüklenir (ana paket büyümesin).
 *
 * Dosya cihazda okunur; hiçbir yere gönderilmez, saklanmaz. Parola
 * yalnızca dosyayı açmak için PDF.js'e verilir ve tutulmaz.
 */

import type { EkstreSayfasi, MetinParcasi } from "./ekstreMaske";
import { M } from "../dil";

export type PdfOkumaSonucu =
  | { readonly tur: "tamam"; readonly sayfalar: readonly EkstreSayfasi[] }
  /** Dosya parolalı; `yanlis` ise verilen parola tutmadı. */
  | { readonly tur: "parola"; readonly yanlis: boolean }
  /** Sayfalarda hiç metin yok: taranmış görüntü olabilir (OCR yapılmaz). */
  | { readonly tur: "metin-yok"; readonly sayfaSayisi: number }
  | { readonly tur: "okunamadi"; readonly sebep: string };

/** Kullandığımız PDF.js yüzeyi (tam tipleri sabitlemeden; testte gerçek modül verilir). */
export interface PdfJs {
  getDocument(src: {
    data: Uint8Array;
    password?: string;
    disableFontFace?: boolean;
    useSystemFonts?: boolean;
    isEvalSupported?: boolean;
    verbosity?: number;
  }): { promise: Promise<PdfBelge>; destroy(): Promise<void> };
}

interface PdfBelge {
  readonly numPages: number;
  getPage(no: number): Promise<PdfSayfa>;
}

interface PdfSayfa {
  getViewport(p: { scale: number }): { width: number; height: number; transform: number[] };
  streamTextContent(): ReadableStream<{ readonly items: readonly unknown[] }>;
}

/**
 * Sayfanın metin parçaları. PDF.js'in `getTextContent`i akışı `for await`
 * ile okur; WebKit'te (iPhone Safari/Brave) `ReadableStream` async
 * yinelenemez ve okuma "undefined is not a function" ile düşer (kullanıcı,
 * 22.10.2026). Aynı akış burada her tarayıcıda olan `getReader()` ile okunur.
 * Tarayıcının genel nesnelerine dokunulmaz.
 */
async function metinParcalari(sayfa: PdfSayfa): Promise<unknown[]> {
  const okuyucu = sayfa.streamTextContent().getReader();
  const out: unknown[] = [];
  try {
    for (;;) {
      const { done, value } = await okuyucu.read();
      if (done) break;
      out.push(...value.items);
    }
  } finally {
    okuyucu.releaseLock();
  }
  return out;
}

interface MetinOgesi {
  readonly str: string;
  readonly transform: readonly number[];
  readonly width: number;
}

function metinOgesiMi(o: unknown): o is MetinOgesi {
  return typeof o === "object" && o !== null && typeof (o as MetinOgesi).str === "string" && Array.isArray((o as MetinOgesi).transform);
}

/** İki 2B dönüşümün çarpımı (PDF sırasıyla: önce `b`, sonra `a`). */
function carp(a: readonly number[], b: readonly number[]): number[] {
  const [a0 = 1, a1 = 0, a2 = 0, a3 = 1, a4 = 0, a5 = 0] = a;
  const [b0 = 1, b1 = 0, b2 = 0, b3 = 1, b4 = 0, b5 = 0] = b;
  return [
    a0 * b0 + a2 * b1,
    a1 * b0 + a3 * b1,
    a0 * b2 + a2 * b3,
    a1 * b2 + a3 * b3,
    a0 * b4 + a2 * b5 + a4,
    a1 * b4 + a3 * b5 + a5,
  ];
}

export async function pdfParcalari(pdfjs: PdfJs, veri: Uint8Array, parola?: string): Promise<PdfOkumaSonucu> {
  // PDF.js veriyi işçiye aktarırken arabelleği devralır: kopyası verilir.
  const gorev = pdfjs.getDocument({
    data: veri.slice(),
    ...(parola !== undefined ? { password: parola } : {}),
    disableFontFace: true,
    useSystemFonts: false,
    isEvalSupported: false,
    // Yalnızca hatalar konsola: yazı tipi dosyası uyarıları çizim içindir,
    // metin çıkarmayı etkilemez (yazı tipi ve CMap dosyaları gönderilmiyor).
    verbosity: 0,
  });
  let belge: PdfBelge;
  try {
    belge = await gorev.promise;
  } catch (e) {
    await gorev.destroy().catch(() => undefined);
    const ad = (e as { name?: unknown }).name;
    if (ad === "PasswordException") return { tur: "parola", yanlis: parola !== undefined };
    if (ad === "InvalidPDFException") return { tur: "okunamadi", sebep: M.iceAktar.pdfGecersiz };
    return { tur: "okunamadi", sebep: e instanceof Error ? e.message : String(e) };
  }
  try {
    const sayfalar: EkstreSayfasi[] = [];
    let toplam = 0;
    for (let no = 1; no <= belge.numPages; no += 1) {
      const sayfa = await belge.getPage(no);
      const gorunum = sayfa.getViewport({ scale: 1 });
      const ogeler = await metinParcalari(sayfa);
      const parcalar: MetinParcasi[] = [];
      for (const o of ogeler) {
        if (!metinOgesiMi(o) || o.str.trim() === "") continue;
        // Görünüm dönüşümü sayfayı sol üst kökene çevirir (döndürme dahil).
        const t = carp(gorunum.transform, o.transform);
        const boy = Math.hypot(t[2] ?? 0, t[3] ?? 0);
        parcalar.push({ x: t[4] ?? 0, y: t[5] ?? 0, genislik: o.width, boy, metin: o.str });
      }
      toplam += parcalar.length;
      sayfalar.push({ no, genislik: gorunum.width, yukseklik: gorunum.height, parcalar });
    }
    if (toplam === 0) return { tur: "metin-yok", sayfaSayisi: belge.numPages };
    return { tur: "tamam", sayfalar };
  } finally {
    // Belge ve işçideki kopyası bellekten atılır.
    await gorev.destroy().catch(() => undefined);
  }
}

let yuklenen: Promise<PdfJs> | undefined;

/** Tarayıcıda PDF.js'i ve işçisini yükler (bir kez). */
export function pdfjsYukle(): Promise<PdfJs> {
  yuklenen ??= Promise.all([
    import("pdfjs-dist/legacy/build/pdf.mjs"),
    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"),
  ]).then(([lib, isci]) => {
    lib.GlobalWorkerOptions.workerSrc = isci.default;
    return lib as unknown as PdfJs;
  });
  yuklenen.catch(() => {
    yuklenen = undefined;
  });
  return yuklenen;
}

/** Ekranın kullandığı okuyucu; testler sahtesini koyar. */
export const pdfOkuyucu = {
  oku: async (veri: Uint8Array, parola?: string): Promise<PdfOkumaSonucu> => pdfParcalari(await pdfjsYukle(), veri, parola),
};
